import React from "react"
import { GridToggle } from "components/GridToggle"
import { PropTypes } from "prop-types"
import classNames from "classnames"
import ReduxFormFieldWrapper from "app/static/frontend/forms/components/ReduxFormFieldWrapper"

import "app/static/stylus/FormStyles.styl"
import "app/static/frontend/forms/stylus/GridToggleField.styl"
import "app/static/frontend/forms/stylus/AccessibleBinaryToggle.styl"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

const accessibleRegistrationFF = isFeatureEnabled("ff_ngg_accessible_registration_form")

// Accessible Binary Selection Component for Yes/No fields
const AccessibleBinaryToggle = ({
    accessibilityId,
    choices,
    value,
    onChange,
    error,
    touched,
    displayErrorWithoutTouch,
    label,
    required = false,
    disabled = false,
    className = "",
}) => {
    const fieldsetId = `${accessibilityId}-fieldset`
    const hasError = error && (displayErrorWithoutTouch || touched)

    return (
        <fieldset
            id={fieldsetId}
            className={classNames("accessible-binary-toggle", className, {
                "has-error": hasError,
            })}
            aria-required={required}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${accessibilityId}-error` : undefined}
        >
            <legend className="sr-only">
                {label}
                {required && " (required)"}
            </legend>
            <div className="binary-options" role="radiogroup" aria-labelledby={fieldsetId}>
                {choices.map((choice, index) => {
                    const optionId = `${accessibilityId}-option-${index}`
                    const isSelected = value === choice.value

                    return (
                        <label
                            key={choice.value}
                            htmlFor={optionId}
                            className={classNames("binary-option-label", {
                                selected: isSelected,
                                disabled: disabled || choice.disabled,
                            })}
                        >
                            <input
                                id={optionId}
                                type="radio"
                                name={`${accessibilityId}-binary-group`}
                                value={choice.value}
                                checked={isSelected}
                                onClick={() => !disabled && !choice.disabled && onChange(choice.value)}
                                disabled={disabled || choice.disabled}
                                aria-describedby={choice.tip ? `${optionId}-tip` : undefined}
                                aria-label={choice.label}
                                className="sr-only"
                            />
                            <span
                                className={classNames("binary-option-button", {
                                    selected: isSelected,
                                    disabled: disabled || choice.disabled,
                                })}
                                aria-hidden="true"
                            >
                                {choice.label}
                            </span>
                            {choice.tip && (
                                <span id={`${optionId}-tip`} className="sr-only">
                                    {choice.tip}
                                </span>
                            )}
                        </label>
                    )
                })}
            </div>
            {hasError && (
                <div id={`${accessibilityId}-error`} className="binary-error-message" role="alert" aria-live="polite">
                    {error}
                </div>
            )}
        </fieldset>
    )
}

export const GridToggleField = (props) => {
    const {
        input: { value },
        meta: { error, warning, touched } = {},
        accessibilityId,
        allowNull,
        banner,
        choices,
        className,
        dataCy,
        displayErrorWithoutTouch,
        isIndentedToggle,
        label,
        labelStyle,
        multi,
        numbered,
        pillWidth,
        selectedStyle,
        shouldRenderNewComponents,
        style,
        tooltipText,
        tooltipPlacement,
        unselectedStyle,
        showAsterisk,
        organizationDesign,
        useAccessibleBinaryToggle = false, // New prop to enable accessible binary selection
        required = false, // New prop to indicate if field is required
    } = props
    // provide a way to pass a custom on change method, or default to redux-form's function
    const onChange = props.customOnChange || props.input.onChange

    const handleChange = (val) => {
        if (multi || !allowNull) {
            onChange(val)
        } else {
            // Either update value or set to null
            onChange(value === val ? null : val)
        }
    }

    // Check if this should use accessible binary toggle (Yes/No fields)
    const isBinarySelection =
        useAccessibleBinaryToggle && choices && choices.length === 2 && !multi && accessibleRegistrationFF

    // For binary selections, use the accessible component
    if (isBinarySelection) {
        return (
            <ReduxFormFieldWrapper
                additionalClassNames={className}
                className="gridtoggle accessible-binary"
                dataCy={dataCy}
                displayErrorWithoutTouch={displayErrorWithoutTouch}
                error={error}
                label={label}
                labelStyle={labelStyle}
                style={style}
                tooltipText={tooltipText}
                touched={touched}
                warning={warning}
                accessibilityId={accessibilityId}
                banner={banner}
                showAsterisk={showAsterisk || required}
            >
                <AccessibleBinaryToggle
                    accessibilityId={accessibilityId}
                    choices={choices}
                    value={value}
                    onChange={handleChange}
                    error={error}
                    touched={touched}
                    displayErrorWithoutTouch={displayErrorWithoutTouch}
                    label={label}
                    required={required || showAsterisk}
                    className={classNames({
                        "gridtoggle-error-outline": error && (displayErrorWithoutTouch || touched),
                        "gridtoggle-warning-outline": warning && (displayErrorWithoutTouch || touched),
                    })}
                />
            </ReduxFormFieldWrapper>
        )
    }

    return (
        <ReduxFormFieldWrapper
            additionalClassNames={className}
            className="gridtoggle"
            dataCy={dataCy}
            displayErrorWithoutTouch={displayErrorWithoutTouch}
            error={error}
            label={label}
            labelStyle={labelStyle}
            style={style}
            tooltipText={tooltipText}
            touched={touched}
            warning={warning}
            accessibilityId={accessibilityId}
            banner={banner}
            showAsterisk={showAsterisk}
        >
            <GridToggle
                isIndentedToggle={isIndentedToggle}
                gridClassName={classNames({
                    "gridtoggle-error-outline": error && (displayErrorWithoutTouch || touched),
                    "gridtoggle-warning-outline": warning && (displayErrorWithoutTouch || touched),
                })}
                options={choices}
                onChange={handleChange}
                value={value}
                multi={multi}
                numbered={numbered}
                bsColWidth={pillWidth}
                selectedStyle={selectedStyle}
                unselectedStyle={unselectedStyle}
                accessibilityId={accessibilityId}
                shouldRenderNewToggles={shouldRenderNewComponents}
                tooltipPlacement={tooltipPlacement}
                organizationDesign={organizationDesign}
            />
        </ReduxFormFieldWrapper>
    )
}

GridToggleField.defaultProps = {
    accessibilityId: "",
    allowNull: true,
    className: "",
    displayErrorWithoutTouch: true,
    label: "",
    numbered: false,
    pillWidth: 4,
    style: {},
    tooltipText: "",
    useAccessibleBinaryToggle: false,
    required: false,
}

GridToggleField.propTypes = {
    accessibilityId: PropTypes.string,
    allowNull: PropTypes.bool,
    choices: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.any.isRequired,
            label: PropTypes.string.isRequired,
            disabled: PropTypes.bool,
            tip: PropTypes.string,
            icon: PropTypes.string,
        }),
    ),
    className: PropTypes.string,
    dataCy: PropTypes.string,
    displayErrorWithoutTouch: PropTypes.bool,
    input: PropTypes.shape({
        value: PropTypes.any,
        onChange: PropTypes.func.isRequired,
    }),
    isIndentedToggle: PropTypes.bool,
    label: PropTypes.string,
    meta: PropTypes.shape({
        error: PropTypes.string,
        warning: PropTypes.string,
        touched: PropTypes.bool.isRequired,
    }),
    labelStyle: PropTypes.object,
    multi: PropTypes.bool,
    numbered: PropTypes.bool,
    pillWidth: PropTypes.number,
    selectedStyle: PropTypes.object,
    shouldRenderNewComponents: PropTypes.bool,
    style: PropTypes.object,
    tooltipText: PropTypes.string,
    unselectedStyle: PropTypes.object,
    organizationDesign: PropTypes.object,
    useAccessibleBinaryToggle: PropTypes.bool,
    required: PropTypes.bool,
}

export default GridToggleField
